import { StandardEvents } from '@shopify/events';

class NamePrint extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.form = document.getElementById(this.dataset.formId);
    if (!this.form) return;
    this.inputs = Array.from(this.querySelectorAll('[data-name-input]'));
    this.addEventListener('change', () => this.update(), { signal });
    this.addEventListener('input', () => this.validateName(), { signal });
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(
      StandardEvents.productSelect,
      this.onVariant,
      { signal }
    );
    this.hidden = false;
    this.update();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.form?.classList.remove('tt-name-print-selected');
  }

  get selected() {
    return Array.from(this.querySelectorAll('[data-name-position]:checked'));
  }

  inputFor(option) {
    return this.inputs.find(input => input.dataset.nameInput === option.dataset.namePosition);
  }

  validateName() {
    for (const input of this.inputs) {
      input.setCustomValidity(input.required && !input.value.trim() ? this.dataset.required : '');
    }
  }

  update() {
    const selected = this.selected;
    for (const input of this.inputs) {
      const active = selected.some(option => option.dataset.namePosition === input.dataset.nameInput);
      input.disabled = !active;
      input.required = active;
      input.closest('[data-name-field]').hidden = !active;
    }
    this.querySelector('[data-name-details]').hidden = selected.length === 0;
    this.form.classList.toggle('tt-name-print-selected', selected.length > 0);
    this.validateName();
    const total = Number(this.dataset.basePrice) + selected.reduce((sum, option) => sum + Number(option.dataset.price), 0);
    this.querySelector('[data-name-total]').textContent = new Intl.NumberFormat(this.dataset.locale, {
      style: 'currency',
      currency: this.dataset.currency,
    }).format(total / 100);
  }

  onVariant = (event) => {
    if (event.target.closest('product-card') && !event.target.closest('product-card').contains(this)) return;
    const pending = event.promise;
    this.variantPromise = pending;
    pending?.then(({ detail }) => {
      if (this.variantPromise !== pending || !detail?.resource) return;
      if (detail.productId && String(detail.productId) !== this.dataset.productId) return;
      this.dataset.basePrice = detail.resource.price;
      this.update();
    }).catch(() => {}).finally(() => {
      if (this.variantPromise === pending) this.variantPromise = null;
    });
  };

  submit = async (event) => {
    if (!this.selected.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.busy) return;
    this.busy = true;
    const errorBox = this.querySelector('[data-name-error]');
    errorBox.hidden = true;
    let buttons = [];
    let added = false;
    try {
      while (this.variantPromise) await this.variantPromise;
      this.validateName();
      if (!this.form.reportValidity()) return;
      const submitButtons = Array.from(this.form.querySelectorAll('button[type="submit"]'));
      if (submitButtons.some(button => button.disabled)) return;
      const data = new FormData(this.form);
      const id = Number(data.get('id'));
      const quantity = Number(data.get('quantity')) || 1;
      if (!id || !Number.isInteger(quantity) || quantity < 1) throw new Error(this.dataset.error);
      const bundle = crypto.randomUUID();
      const properties = { _tt_name_bundle: bundle };
      for (const [key, value] of data.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }
      const selected = this.selected;
      for (const option of selected) {
        const input = this.inputFor(option);
        if (!input) throw new Error(this.dataset.error);
        properties[input.dataset.propertyKey] = input.value.trim();
      }
      const parent = { id, quantity, properties };
      if (data.get('selling_plan')) parent.selling_plan = data.get('selling_plan');
      const items = [parent, ...selected.map(option => ({
        id: Number(option.value),
        quantity,
        parent_id: id,
        properties: {
          _tt_name_bundle: bundle,
          _tt_name_fee: '1',
          [this.dataset.nameKey]: this.inputFor(option).value.trim(),
          [this.dataset.positionKey]: option.dataset.position,
        },
      }))];
      const payload = new FormData();
      items.forEach((item, index) => {
        for (const [key, value] of Object.entries(item)) {
          if (key === 'properties') {
            for (const [property, content] of Object.entries(value)) {
              payload.append(`items[${index}][properties][${property}]`, content);
            }
          } else {
            payload.append(`items[${index}][${key}]`, value);
          }
        }
      });
      buttons = submitButtons;
      buttons.forEach(button => { button.disabled = true; });
      this.form.setAttribute('aria-busy', 'true');
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: payload,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
      added = true;
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      errorBox.textContent = error.message || this.dataset.error;
      errorBox.hidden = false;
    } finally {
      if (!added) {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        buttons.forEach(button => { button.disabled = false; });
      }
    }
  };
}

if (!customElements.get('tt-name-print')) customElements.define('tt-name-print', NamePrint);
